import React from "react";
import { TouchableOpacity } from "react-native";
import { graphql, Mutation, withApollo } from "react-apollo";
import styled, { withTheme } from "styled-components/native";
import { defineMessages } from "react-intl";
import { delay } from "lodash";
import { Avatar, Button, Container } from "cci-component";
import { getSessionInformation } from "cci-data";
import { hasPermissions, logoutMutation } from "cci-logic";
import { withRouter } from "cci-utility";

/* Text for business, office and user information */
const Text = styled.Text`
  font-family: ${props => props.theme.fontFamily};
  font-size: ${props => props.fontSize}px;
  color: ${props => props.theme.fontColor};
`;

const messages = defineMessages({
  logout: {
    id: "Logout",
    defaultMessage: "Logout"
  }
});

/**
 * Shows the logged in user's avatar, their name and the business and office that the are working with.
 * 
 * Props:
 * - avatarSize: Size of the avatar image in pixels.
 * - infoAlign: Can be "row" when the user name and business needs to be to the right of the avatar or "column" when it needs to be below the avatar.
 * - infoMargins: Margins for the container that has the user name, business and office. Can be used to provide spacing between avatar and user info.
 * - userFontSize: Font size for the user's name.
 * - businessFontSize: Font size for the business and office name.
 */
const UserInfo = props => {
  const { theme, client, data, onBusinessTap, onOfficeTap } = props;

  if (data.loading === true) {
    // This happens when logging out and then logging back in for some reason.
    return null;
  }

  const sessionInformation = data.sessionInformation,
    userContact = sessionInformation.user.contact,
    middleName = userContact.middleName ? userContact.middleName + " " : "",
    businessContact = sessionInformation.currentBusiness.contact,
    officeContact = sessionInformation.currentOffice.contact,
    avatarSelected =
      theme.avatarSelected === "business"
        ? businessContact.photoId
        : theme.avatarSelected === "office"
        ? officeContact.photoId
        : theme.avatarSelected === "user"
        ? userContact.photoId
        : null;

  return (
    <React.Fragment>
      {/* User's photo */}
      {theme.avatarSelected && (
        <Avatar
          size={theme.avatarSize || props.avatarSize}
          imageId={avatarSelected}
        />
      )}

      <Container
        flexDirection="column"
        alignItems={props.infoAlign}
        margins={props.infoMargins}
      >
        {/* User's name */}
        <Text fontSize={theme.userNameInfoFontSize || props.userFontSize}>
          {`${userContact.firstName} ${middleName}${userContact.lastName}`}
        </Text>

        <Container flexDirection="row">
          {/* Business */}
          <TouchableOpacity
            disabled={
              !hasPermissions(sessionInformation.user.permissions, {
                objects: "Business",
                actions: ["selectBusiness"]
              })
            }
            onPress={onBusinessTap}
          >
            <Text fontSize={theme.userInfoFontSize || props.businessFontSize}>
              {businessContact.companyName}
            </Text>
          </TouchableOpacity>

          <Text fontSize={theme.userInfoFontSize || props.businessFontSize}>
            {" - "}
          </Text>

          {/* Office */}
          <TouchableOpacity
            disabled={
              !hasPermissions(sessionInformation.user.permissions, {
                objects: "Office",
                actions: ["selectOffice"]
              })
            }
            onPress={onOfficeTap}
          >
            <Text fontSize={theme.userInfoFontSize || props.businessFontSize}>
              {officeContact.alias}
            </Text>
          </TouchableOpacity>
        </Container>

        {/* LOGOUT */}
        {theme.logoutOnUserInfo && (
          <Container margins="15px 0 0">
            <Mutation mutation={logoutMutation}>
              {logout => (
                <Button
                  ui="link"
                  leftIcon="sign-out"
                  fontColor={theme.mainMenuFontColor || theme.fontColor}
                  title={messages.logout}
                  onPress={() => {
                    logout({
                      errorPolicy: "ignore",
                      context: { client: props.client }
                    });
                    // TODO is this a hack?
                    delay(() => props.history.push("/"), 50);
                  }}
                />
              )}
            </Mutation>
          </Container>
        )}
      </Container>
    </React.Fragment>
  );
};

UserInfo.defaultProps = {
  avatarSize: 40,
  userFontSize: "12px",
  businessFontSize: "10px",
  infoAlign: "flex-start",
  infoMargins: "0 0 0 8px"
};

export default withApollo(
  withRouter(
    withTheme(
      graphql(getSessionInformation, { fetchPolicy: "cache-only" })(UserInfo)
    )
  )
);
